import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import ProfileField from './profile/ProfileField';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { PROFILE_BLOCK } from '../../../../services/utilityFunctions';
import { getValidationRules } from '../../helpers/helper';
import ErrorMessage from '../../../../commonComponents/forms/ErrorMessage';

function stateFromProps(member = {}) {
  return {
    hobbyList: member.hobbyList || [],
  };
}

/**
 * Form to update the current logged user
 */
function HobbiesForm({ t, onMemberChange, member, onSkipStep }) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const [state, setState] = useState(stateFromProps(member));
  const [errors, setErrors] = useState({
    hobbies: false,
  });
  useEffect(() => setState(stateFromProps(member)), [member]);

  const formId = 'hobbiesForm';
  const validationRules = useMemo(
    () => getValidationRules(requiredFieldsPerBlock, PROFILE_BLOCK.HOBBIES, {}),
    [getValidationRules, requiredFieldsPerBlock],
  );

  const handleSubmit = async () => {
    if (validationRules?.hobbies?.required && state?.hobbyList?.length === 0) {
      setErrors(prevErrors => ({
        ...prevErrors,
        hobbies: true,
      }));
      return;
    }
    const params = {
      hobbyList: state.hobbyList,
    };
    onMemberChange(params);
  };

  const handleChange = useCallback((field, value) => {
    setErrors(prevErrors => ({
      ...prevErrors,
      hobbies: false,
    }));
    setState(prevState => ({ ...prevState, hobbyList: value }));
  }, []);

  const inputProps = useCallback(() => undefined, []);

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <div className="row profile-skipStep">
              <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                <FormSubtitle title={t('profile_tab_hobbies')} />
              </div>
              {onSkipStep && (
                <div className="col-6 skip-wrapper btn-wrapper">
                  <SkipStepButton onClick={onSkipStep} />
                </div>
              )}
            </div>
            <ProfileField
              field="hobbies"
              type="tags"
              value={state.hobbyList}
              onChange={handleChange}
              inputProps={inputProps}
              block={PROFILE_BLOCK.HOBBIES}
              requiredFieldsPerBlock={requiredFieldsPerBlock}
              labelClassName={`col-lg-2 col-sm-12 ${
                errors?.hobbies ? 'label-error' : ''
              }`}
            />
            {errors?.hobbies && <ErrorMessage />}
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

HobbiesForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func,
};

HobbiesForm.defaultProps = {
  onSkipStep: null,
};

export default translate(HobbiesForm);
